'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { requestFn } from '@openapi-qraft/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { toast } from 'sonner';

import { components, createAPIClient, operations, Services } from '@/api/v1/query';
import { useAuthCtx } from '@/components/providers/auth-provider';

export type ApiSchemas = components['schemas'];
export type ApiOperations = operations;

interface Context {
  api: Services;
}

const ApiContext = createContext<Context | null>(null);

export function useApiV1Ctx() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within a AuthedApiProvider');
  }
  return context;
}

export function ApiV1Provider({ children }: { children: React.ReactNode }) {
  const { token } = useAuthCtx();

  const tokenRef = useRef(token);
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 2,
          },
        },
        queryCache: new QueryCache({
          onSettled: (_data, error, query) => {
            if (error) {
              posthog.captureException(error, {
                kind: 'query',
                queryKey: query.queryKey,
              });
              console.error('query error', error);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            posthog.captureException(error, {
              kind: 'mutation',
              mutationKey: mutation.options.mutationKey,
            });

            console.error('mutation error', error);

            if (mutation.options.meta?.toastError) {
              toast.error(mutation.options.meta.toastError);
            } else if (mutation.options.meta?.toastError === false) {
              return;
            } else {
              toast.error('Sorry, something went wrong');
            }
          },
        }),
      }),
    []
  );
  const [api] = useState(() => {
    const client = createAPIClient({
      baseUrl: process.env.NEXT_PUBLIC_API_V1_BASE_URL!,
      queryClient,
      requestFn: async (schema, requestInfo) => {
        return await requestFn(schema, requestInfo, {
          fetch: (url, options) => {
            return fetchFn(url, options, tokenRef);
          },
        });
      },
    });
    return client;
  });

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiContext.Provider value={{ api }}>{children}</ApiContext.Provider>
    </QueryClientProvider>
  );
}

export class HttpError extends Error {
  status: number;
  method: string;
  url: string;
  body?: unknown;
  detail?: string;

  constructor(message: string, status: number, method: string, url: string, body?: unknown) {
    const pathname = new URL(url).pathname;
    let bodyStr = '';
    if (body) {
      bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
    }
    super(`HTTP ${method} ${pathname} failed with status ${status}`);
    this.name = 'HttpError';
    this.status = status;
    this.method = method;
    this.url = url;
    this.body = bodyStr;

    if (
      typeof body === 'object' &&
      body !== null &&
      'detail' in body &&
      typeof (body as { detail: unknown }).detail === 'string'
    ) {
      this.detail = (body as { detail: string }).detail;
    }
  }
}

async function fetchFn(url: RequestInfo | URL, options: RequestInit | undefined, token: { current: string | null }) {
  // Dont assume json since we may be uploading files (FormData)
  const isFormData = options?.body instanceof FormData;

  const headers: Record<string, string> = {
    ...((options?.headers as Record<string, string>) || {}),
    Authorization: `Bearer ${token.current}`,
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
  if (!response.ok) {
    const body = await parseResponseBody(response);
    throw new HttpError(response.statusText, response.status, options?.method || 'GET', response.url, body);
  }
  return response;
}

export async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch {
    // Return empty string on parse errors
    return '';
  }
}
